import {
  ConsultationDocument,
  GetResponseDocumentRequest,
  useConsultationDocument,
  useResponseDocument,
} from 'api';

export type useDocumentDownloadReturn = {
  openResponseDocument: (documentRequest: GetResponseDocumentRequest | undefined) => void;
  openConsultationDocument: (document: ConsultationDocument | undefined) => void;
  downloadConsultationDocument: (document: ConsultationDocument | undefined) => void;
  openMeasuringTool: (
    consultationId: number | undefined,
    document: ConsultationDocument | undefined
  ) => void;
};

export const useFileDownload = (): useDocumentDownloadReturn => {
  const { mutate: downloadConsultationDocumentApi } = useConsultationDocument();
  const { mutate: downloadResponseDocumentApi } = useResponseDocument();

  const openResponseDocument = (documentRequest: GetResponseDocumentRequest | undefined) => {
    if (!documentRequest) {
      return;
    }

    downloadResponseDocumentApi(documentRequest, {
      onSuccess: (response) => openFileInNewTab(response.documentUri),
    });
  };

  const openConsultationDocument = (document: ConsultationDocument | undefined) => {
    if (!document) {
      return;
    }

    downloadConsultationDocumentApi(
      {
        documentId: document.documentId,
        consultationId: document.consultationId,
        documentSource: document.documentSource,
      },
      {
        onSuccess: (response) => openFileInNewTab(response.documentUri),
      }
    );
  };

  const downloadConsultationDocument = (document: ConsultationDocument | undefined) => {
    if (!document) {
      return;
    }

    downloadConsultationDocumentApi(
      {
        documentId: document.documentId,
        consultationId: document.consultationId,
        documentSource: document.documentSource,
      },
      {
        onSuccess: (response) =>
          downloadFile(
            response.documentUri,
            document?.fileName ?? `${document.applicationReferenceNumber}.pdf`
          ),
      }
    );
  };

  const openMeasuringTool = (
    consultationId: number | undefined,
    document: ConsultationDocument | undefined
  ) => {
    if (!consultationId || !document) {
      return;
    }

    downloadConsultationDocumentApi(
      {
        documentId: document.documentId,
        consultationId: document.consultationId,
        documentSource: document.documentSource,
      },
      {
        onSuccess: (response) =>
          openMeasuringToolInNewTab(consultationId, response.documentUri),
      }
    );
  };

  const openFileInNewTab = (path: string): void => {
    if (path) {
      window.open(path, '_blank');
    }
  };

  const openMeasuringToolInNewTab = (consultationId: number, path: string) => {
    if (path) {
      const url = path.split('?')[0];
      const queryString = path.split('?')[1];
      if (!queryString || queryString.indexOf('sig=') === -1) {
        return;
      }
      const partEncodedQueryString = queryString
        .split('sig=')[0]
        .concat('sig=')
        .concat(encodeURIComponent(queryString.split('sig=')[1].substring(0)));
      window.open(
        `${window.location.origin}/consultation/${consultationId}/measure/${encodeURIComponent(
          url.concat('?').concat(partEncodedQueryString)
        )}`,
        '_blank'
      );
    }
  };

  const downloadFile = (path: string, filename: string) => {
    fetch(path)
      .then((response) => response.blob())
      .then((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
      });
  };

  return {
    openResponseDocument,
    openConsultationDocument,
    downloadConsultationDocument,
    openMeasuringTool,
  };
};
